import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import {
  normalizeStockPositionOtherFilter,
  STOCK_POSITION_OTHER_FILTER,
} from "../utils/aggregateStockPositions";

/** Stack multi-word header labels vertically to save horizontal space. */
function StackedHeader({ label, align = "left" }) {
  const words = String(label).trim().split(/\s+/).filter(Boolean);
  return (
    <th
      className={cn(
        "px-3 py-2 font-semibold leading-tight align-bottom",
        align === "right" ? "text-right" : "text-left"
      )}
    >
      {words.length <= 1 ? (
        label
      ) : (
        <span className="inline-flex flex-col">
          {words.map((word, i) => (
            <span key={`${word}-${i}`}>{word}</span>
          ))}
        </span>
      )}
    </th>
  );
}

export function StockPositionTable({
  data,
  systemSummaries = [],
  page,
  pageSize,
  onPageChange,
  totalItems,
  totalPages: totalPagesProp,
  otherFilter = "All",
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const otherFilterNorm = normalizeStockPositionOtherFilter(otherFilter);
  // Mobile system groups start collapsed; keys present & true = expanded
  const [expandedSystems, setExpandedSystems] = useState({});

  const isServerPaged = typeof totalItems === "number";
  const totalPages = Math.max(
    1,
    totalPagesProp ?? Math.ceil((isServerPaged ? totalItems : data.length) / pageSize)
  );
  const pagedRows = useMemo(() => {
    if (isServerPaged) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, isServerPaged]);

  const groupedSections = useMemo(() => {
    const summaryBySystem = new Map();
    (systemSummaries || []).forEach((item) => {
      const key = item?.systemName || "";
      if (!key) return;
      summaryBySystem.set(key, Number(item.total) || 0);
    });

    const groups = new Map();
    const encounterOrder = [];
    pagedRows.forEach((row) => {
      const key = row.systemName || "—";
      if (!groups.has(key)) {
        groups.set(key, []);
        encounterOrder.push(key);
      }
      groups.get(key).push(row);
    });

    const summaryOrder = (systemSummaries || [])
      .map((item) => item?.systemName)
      .filter((name) => name && groups.has(name));
    const orderedKeys = [
      ...summaryOrder,
      ...encounterOrder.filter((key) => !summaryOrder.includes(key)),
    ];

    return orderedKeys.map((systemName) => {
      const rows = groups.get(systemName) || [];
      const totalStock = rows.reduce((sum, row) => sum + (Number(row.totalStock) || 0), 0);
      const availableStock = rows.reduce((sum, row) => sum + (Number(row.availableStock) || 0), 0);
      const reservedStock = rows.reduce((sum, row) => sum + (Number(row.reservedStock) || 0), 0);
      const reservedStockValue = rows.reduce(
        (sum, row) => sum + (Number(row.reservedStockValue) || 0),
        0
      );
      const serviceSafetyStock = rows.reduce(
        (sum, row) =>
          sum +
          (Number(row.serviceSafetyStock ?? row.maintenanceReserve) || 0),
        0
      );
      const salesSafetyStock = rows.reduce((sum, row) => sum + (Number(row.salesSafetyStock) || 0), 0);
      const defectiveStock = rows.reduce((sum, row) => sum + (Number(row.defectiveStock) || 0), 0);
      const defectiveValue = rows.reduce((sum, row) => sum + (Number(row.defectiveValue) || 0), 0);
      const serviceSafetyStockValue = rows.reduce(
        (sum, row) =>
          sum +
          (Number(row.serviceSafetyStockValue ?? row.maintenanceReserveValue) || 0),
        0
      );
      const salesSafetyStockValue = rows.reduce(
        (sum, row) => sum + (Number(row.salesSafetyStockValue) || 0),
        0
      );
      const maintenanceReserve = serviceSafetyStock;

      let availableStockValue;
      if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
        availableStockValue = defectiveValue;
      } else if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
        availableStockValue = serviceSafetyStockValue;
      } else if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
        availableStockValue = salesSafetyStockValue;
      } else if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.RESERVED) {
        availableStockValue = reservedStockValue;
      } else if (summaryBySystem.has(systemName)) {
        availableStockValue = summaryBySystem.get(systemName);
      } else {
        availableStockValue = rows.reduce((sum, row) => sum + (Number(row.availableStockValue) || 0), 0);
      }

      return {
        systemName,
        availableStockValue,
        totalStock,
        availableStock,
        reservedStock,
        maintenanceReserve,
        salesSafetyStock,
        defectiveStock,
        rows,
      };
    });
  }, [pagedRows, systemSummaries, otherFilterNorm]);

  const stockValueColumnLabel =
    otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEFECTIVE
      ? t("inventory.defectiveValue")
      : otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY
        ? t("inventory.serviceSafetyStockValue")
        : otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY
          ? t("inventory.salesSafetyStockValue")
          : otherFilterNorm === STOCK_POSITION_OTHER_FILTER.RESERVED
            ? t("inventory.reservedStockValue")
            : t("inventory.availableStockValue");

  const rowStockValue = (row) => {
    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
      return Number(row.defectiveValue) || 0;
    }
    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
      return (
        Number(row.serviceSafetyStockValue ?? row.maintenanceReserveValue) || 0
      );
    }
    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
      return Number(row.salesSafetyStockValue) || 0;
    }
    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.RESERVED) {
      return Number(row.reservedStockValue) || 0;
    }
    return Number(row.availableStockValue) || 0;
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(val) || 0);
  };

  const formatQty = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toLocaleString() : "—";
  };

  const handleRowClick = (item) => {
    if (!item?.productId) return;
    navigate(`/inventory-management/stock-position/${item.productId}`);
  };

  const toggleSystem = (systemName) => {
    setExpandedSystems((prev) => ({
      ...prev,
      [systemName]: !prev[systemName],
    }));
  };

  const SystemGroupHeader = ({
    systemName,
    availableStockValue,
    totalStock,
    availableStock,
    reservedStock,
    maintenanceReserve,
    salesSafetyStock,
    defectiveStock,
    expanded,
    onToggle,
    className,
  }) => (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className={cn(
        "w-full text-left border-l-4 border-primary bg-[color-mix(in_srgb,var(--color-primary)_8%,white)] px-4 py-2.5 space-y-1.5 cursor-pointer active:opacity-90",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-extrabold tracking-wide text-primary block truncate min-w-0">
          {systemName}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-primary" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
            {t("inventory.total")}
          </div>
          <div className="text-xs font-extrabold text-foreground font-mono">
            {formatQty(totalStock)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
            {t("status.available")}
          </div>
          <div className="text-xs font-extrabold text-primary font-mono">
            {formatQty(availableStock)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
            {t("inventory.customerReserved")}
          </div>
          <div className="text-xs font-extrabold text-foreground font-mono">
            {formatQty(reservedStock)}
          </div>
        </div>
        {salesSafetyStock > 0 && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
              {t("inventory.safetyStock")}
            </div>
            <div className="text-xs font-extrabold text-foreground font-mono">
              {formatQty(salesSafetyStock)}
            </div>
          </div>
        )}
        {maintenanceReserve > 0 && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
              {t("inventory.serviceReserved")}
            </div>
            <div className="text-xs font-extrabold text-foreground font-mono">
              {formatQty(maintenanceReserve)}
            </div>
          </div>
        )}
        
        {defectiveStock > 0 && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
              {t("inventory.defective")}
            </div>
            <div className="text-xs font-extrabold text-rose-600 font-mono">
              {formatQty(defectiveStock)}
            </div>
          </div>
        )}
        <div className="text-right ml-auto">
          <div className="text-[10px] font-bold uppercase tracking-wide per-file-hint leading-none">
            {stockValueColumnLabel}
          </div>
          <div className="text-sm font-extrabold text-primary font-mono">
            {formatCurrency(availableStockValue)}
          </div>
        </div>
      </div>
    </button>
  );

  const MetricRow = ({ label, value, valueClassName }) => (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint">{label}</span>
      <span className={cn("font-semibold text-foreground text-right", valueClassName)}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="font-sans">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className={ui.tableHead}>
              <StackedHeader label={t("inventory.product")} />
              <StackedHeader label={t("inventory.total")} align="right" />
              <StackedHeader label={t("inventory.customerReserved")} align="right" />
              <StackedHeader label={t("inventory.safetyStock")} align="right" />
              <StackedHeader label={t("inventory.serviceReserved")} align="right" />
              <StackedHeader label={t("inventory.defective")} align="right" />
              <StackedHeader label={t("status.available")} align="right" />
              <StackedHeader label={stockValueColumnLabel} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-text-border)/35">
            {groupedSections.map((section) => (
              <React.Fragment key={section.systemName}>
                <tr className="bg-[color-mix(in_srgb,var(--color-primary)_8%,white)]">
                  <td className="px-0 py-0">
                    <div className="flex items-center gap-3 border-l-4 border-primary px-4 py-2.5 h-full">
                      <span className="text-sm font-extrabold tracking-wide text-primary truncate">
                        {section.systemName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <span className="text-sm font-extrabold text-foreground font-mono">
                      {formatQty(section.totalStock)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <span className="text-sm font-extrabold text-foreground font-mono">
                      {formatQty(section.reservedStock)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {section.salesSafetyStock > 0 && (
                      <span className="text-sm font-extrabold text-foreground font-mono">
                        {formatQty(section.salesSafetyStock)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {section.maintenanceReserve > 0 && (
                      <span className="text-sm font-extrabold text-foreground font-mono">
                        {formatQty(section.maintenanceReserve)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {section.defectiveStock > 0 && (
                      <span className="text-sm font-extrabold text-rose-600 font-mono">
                        {formatQty(section.defectiveStock)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <span className="text-sm font-extrabold text-primary font-mono">
                      {formatQty(section.availableStock)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <span className="text-base font-extrabold text-primary font-mono">
                      {formatCurrency(section.availableStockValue)}
                    </span>
                  </td>
                </tr>
                {section.rows.map((row) => (
                  <tr
                    key={row.id || row.productId}
                    onClick={() => handleRowClick(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="hover:bg-workspace/20 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3.5">
                      <span className="font-semibold text-foreground block truncate leading-tight">
                        {row.productName}
                      </span>
                      <span className="text-xs per-file-hint block mt-0.5 font-mono">
                        {row.partNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground whitespace-nowrap">
                      {formatQty(row.totalStock)}
                    </td>
                    <td className="px-4 py-3.5 text-right per-file-hint whitespace-nowrap">
                      {row.reservedStock > 0 ? formatQty(row.reservedStock) : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-right per-file-hint whitespace-nowrap">
                      {row.salesSafetyStock > 0 ? formatQty(row.salesSafetyStock) : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-right per-file-hint whitespace-nowrap">
                      {(Number(row.serviceSafetyStock ?? row.maintenanceReserve) || 0) > 0
                        ? formatQty(row.serviceSafetyStock ?? row.maintenanceReserve)
                        : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-right text-rose-600 whitespace-nowrap">
                      {row.defectiveStock > 0 ? formatQty(row.defectiveStock) : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-primary whitespace-nowrap">
                      {formatQty(row.availableStock)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-sm text-foreground whitespace-nowrap">
                      {formatCurrency(rowStockValue(row))}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — system groups collapsed by default */}
      <div className="md:hidden space-y-3 p-3">
        {groupedSections.map((section) => {
          const isExpanded = !!expandedSystems[section.systemName];
          return (
            <section
              key={section.systemName}
              className="rounded-xl border border-(--color-text-border)/60 overflow-hidden bg-white shadow-xs"
            >
              <SystemGroupHeader
                systemName={section.systemName}
                availableStockValue={section.availableStockValue}
                totalStock={section.totalStock}
                availableStock={section.availableStock}
                reservedStock={section.reservedStock}
                maintenanceReserve={section.maintenanceReserve}
                salesSafetyStock={section.salesSafetyStock}
                defectiveStock={section.defectiveStock}
                expanded={isExpanded}
                onToggle={() => toggleSystem(section.systemName)}
              />
              {isExpanded && (
                <ul className="space-y-2.5 p-2.5 border-t border-(--color-text-border)/25">
                  {section.rows.map((row) => (
                    <li
                      key={row.id || row.productId}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs active:bg-workspace/40 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-snug truncate">
                          {row.productName}
                        </p>
                        <p className="text-xs per-file-hint font-mono mt-0.5">
                          {row.partNumber || "—"}
                        </p>
                      </div>
                      <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25">
                        <MetricRow label={t("inventory.total")} value={formatQty(row.totalStock)} />
                        <MetricRow
                          label={t("inventory.customerReserved")}
                          value={row.reservedStock > 0 ? formatQty(row.reservedStock) : "—"}
                        />
                        <MetricRow
                          label={t("inventory.safetyStock")}
                          value={
                            row.salesSafetyStock > 0
                              ? formatQty(row.salesSafetyStock)
                              : "—"
                          }
                        />
                        <MetricRow
                          label={t("inventory.serviceReserved")}
                          value={
                            (Number(row.serviceSafetyStock ?? row.maintenanceReserve) || 0) > 0
                              ? formatQty(row.serviceSafetyStock ?? row.maintenanceReserve)
                              : "—"
                          }
                        />
                        <MetricRow
                          label={t("inventory.defective")}
                          value={
                            row.defectiveStock > 0 ? formatQty(row.defectiveStock) : "—"
                          }
                          valueClassName={
                            row.defectiveStock > 0 ? "text-rose-600" : undefined
                          }
                        />
                        <MetricRow
                          label={t("status.available")}
                          value={formatQty(row.availableStock)}
                          valueClassName="text-primary"
                        />
                        <MetricRow
                          label={stockValueColumnLabel}
                          value={formatCurrency(rowStockValue(row))}
                          valueClassName="text-sm font-semibold"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      <div className="px-4 py-3">
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={isServerPaged ? totalItems : data.length}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
